import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import inventoryHelpers from '../../helpers/inventory';
import { DataGrid } from '@mui/x-data-grid';
import { Button, TextField } from '@mui/material';
import './inventory-allocator.css';
import InventoryAllocatorHelpPopup from './InventoryAllocatorHelpPopup';

const defaultColumnAttributes = {
    flex: 1,
    editable: false,
    minWidth: 80
};

const customers = [
    "academy",
    "amazonDirect",
    "chewy",
    "facebook",
    "kohls",
    "lowes",
    "petco",
    "petsmart",
    "shopify",
    "target",
    "tractorSupply",
    "walmartMarketplace",
    "wayfair"
];

const InventoryAllocator = props => {
    const [skuAllocationInfos, setSkuAllocationInfos] = useState([]);
    const [calculatedInventoryLevels, setCalculatedInventoryLevels] = useState([]);
    const [allocationChanges, setAllocationChanges] = useState([]);
    const [displayedRows, setDisplayedRows] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState(-1);
    // const [errorRows, setErrorRows] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [displayHelpModal, setDisplayHelpModal] = useState(false);
       

    useEffect(async () => {
        props.setLoading(true);
        try {
            const allocationInfos = await inventoryHelpers.getInventoryAllocations(props.token);
            setSkuAllocationInfos(allocationInfos);
            const calculatedInventory = allocationInfos.map((allocationInfo, index) => {
                const calculatedInventoryLevelsForSku = calculateInventoryLevels(allocationInfo);
                return {
                    ...calculatedInventoryLevelsForSku,
                    id: index,
                    sku: allocationInfo.sku,
                    upc: allocationInfo.upc,
                    itemStatus: allocationInfo.itemStatus,
                    inventoryLevel: allocationInfo.inventoryLevel
                };
            });
            setCalculatedInventoryLevels(calculatedInventory);
            setDisplayedRows(calculatedInventory);
        } catch (error) {
            props.toast.error("Failed to retrieve inventory allocations, please contact a developer");
            console.log(error);
        }
        
        props.setLoading(false);
    }, []);


    const selectRow = (params, event, details) => {
        if (params.id === selectedRowId || params.id === "displayed-allocation-row") {
            return;
        }
        
        const displayedAllocationRow = {
            ...skuAllocationInfos[params.id],
            id: "displayed-allocation-row",
            sku: "",
            upc: "",
            inventoryLevel: ""
        };

        let newDisplayedRows;

        if (selectedRowId === -1) {  // add new row beneath selected row
            const displayPositionOfSelectedRow = displayedRows.findIndex(row => row.id === params.id);
            newDisplayedRows = [
                ...displayedRows.slice(0, displayPositionOfSelectedRow + 1),
                displayedAllocationRow,
                ...displayedRows.slice(displayPositionOfSelectedRow + 1, displayedRows.length)
            ];
        } else {    // remove previously added row, then add new row beneath selected row
            const displayPositionOfPreviouslySelectedRow = displayedRows.findIndex(row => row.id === selectedRowId);
            newDisplayedRows = [
                ...displayedRows.slice(0, displayPositionOfPreviouslySelectedRow + 1),
                ...displayedRows.slice(displayPositionOfPreviouslySelectedRow + 2, displayedRows.length)
            ];

            const displayPositionOfNewSelectedRow = newDisplayedRows.findIndex(row => row.id === params.id);

            newDisplayedRows = [
                ...newDisplayedRows.slice(0, displayPositionOfNewSelectedRow + 1),
                displayedAllocationRow,
                ...newDisplayedRows.slice(displayPositionOfNewSelectedRow + 1, displayedRows.length)
            ];
        }
        
        setDisplayedRows(newDisplayedRows);
        setSelectedRowId(params.id);
    };


    const calculateInventoryLevels = (allocationInfo) => {
        const sumOfSpecifiedPercentages = customers.map(customer => allocationInfo[customer])
                                                   .filter(allocation => !["TRUE", "FALSE"].includes(allocation))
                                                   .reduce((x, y) => parseInt(x) + parseInt(y), 0);
        
        const numberOfTrues = customers.filter(customer => allocationInfo[customer] === "TRUE").length;
        const trueCalculatedInventory = Math.floor(allocationInfo.inventoryLevel * (100 - sumOfSpecifiedPercentages) / (100 * numberOfTrues));
        const calculatedInventoryRow = {};

        customers.forEach(customer => {
            const customerAllocation = allocationInfo[customer];
            if (customerAllocation === "TRUE") {
                calculatedInventoryRow[customer] = trueCalculatedInventory;
            } else if (customerAllocation === "FALSE") {
                calculatedInventoryRow[customer] = "";
            } else {
                calculatedInventoryRow[customer] = Math.floor(allocationInfo.inventoryLevel * parseInt(allocationInfo[customer]) / 100);
            }
        });

        return calculatedInventoryRow;
    };


    // Whenever a value changes, if the value is TRUE or FALSE or a valid percentage, then recalculate inventory
    const updateCustomerAllocation = (value, event) => {
        if (!event || event.id !== "displayed-allocation-row") {
            return;
        }

        const formattedValue = ["TRUE", "FALSE"].includes(value.toUpperCase()) ? value.toUpperCase() : value;
        const selectedCustomer = event.field;
        const newDisplayedRows = JSON.parse(JSON.stringify(displayedRows));
        const indexOfModifiedRow = displayedRows.findIndex(row => row.id === event.id);
        newDisplayedRows[indexOfModifiedRow][selectedCustomer] = formattedValue;

        const selectedRow = event.row;
        selectedRow[selectedCustomer] = formattedValue;
        selectedRow.inventoryLevel = skuAllocationInfos[selectedRowId].inventoryLevel;
        
        if (["TRUE", "FALSE"].includes(formattedValue)) {
            addAllocationChange(skuAllocationInfos[selectedRowId].sku, selectedCustomer, formattedValue);
            const calculatedInventoryRow = {
                ...calculateInventoryLevels(selectedRow),
                id: selectedRowId,
                inventoryLevel: skuAllocationInfos[selectedRowId].inventoryLevel,
                sku: skuAllocationInfos[selectedRowId].sku,
                upc: skuAllocationInfos[selectedRowId].upc
            };
            newDisplayedRows[indexOfModifiedRow - 1] = calculatedInventoryRow;

            const newAllocations = JSON.parse(JSON.stringify(skuAllocationInfos));
            newAllocations[selectedRowId][selectedCustomer] = formattedValue;
            setSkuAllocationInfos(newAllocations);

            const newCalculatedInventoryLevels = JSON.parse(JSON.stringify(calculatedInventoryLevels));
            newCalculatedInventoryLevels[selectedRowId] = calculatedInventoryRow;
            setCalculatedInventoryLevels(newCalculatedInventoryLevels);
        } else if (!isNaN(parseInt(value))) {
            if (value >= 0 && value <= 100) {
                // compare to other values in row to see if still valid
                const otherCustomers = customers.filter(customer => customer !== selectedCustomer);
                const selectedRowAllocations = otherCustomers.map(customer => selectedRow[customer]);
                const numericAllocations = selectedRowAllocations.filter(allocation => !isNaN(parseInt(allocation)));
                const sumOfNumericAllocations = numericAllocations.reduce((x, y) => parseInt(x) + parseInt(y), parseInt(value));
                if (sumOfNumericAllocations <= 100) {
                    addAllocationChange(skuAllocationInfos[selectedRowId].sku, selectedCustomer, formattedValue);
                    const calculatedInventoryRow = {
                        ...calculateInventoryLevels(selectedRow),
                        id: selectedRowId,
                        inventoryLevel: skuAllocationInfos[selectedRowId].inventoryLevel,
                        sku: skuAllocationInfos[selectedRowId].sku,
                        upc: skuAllocationInfos[selectedRowId].upc
                    };
                    newDisplayedRows[indexOfModifiedRow - 1] = calculatedInventoryRow;
                    
                    const newAllocations = JSON.parse(JSON.stringify(skuAllocationInfos));
                    newAllocations[selectedRowId][selectedCustomer] = formattedValue;
                    setSkuAllocationInfos(newAllocations);

                    const newCalculatedInventoryLevels = JSON.parse(JSON.stringify(calculatedInventoryLevels));
                    newCalculatedInventoryLevels[selectedRowId] = calculatedInventoryRow;
                    setCalculatedInventoryLevels(newCalculatedInventoryLevels);
                }
            }
        }
        
        setDisplayedRows(newDisplayedRows);
        return formattedValue;
    };

    const addAllocationChange = (sku, customer, allocation) => {
        const index = allocationChanges.findIndex(change => change.sku === sku && change.customer === customer);
        const newAllocationChanges = JSON.parse(JSON.stringify(allocationChanges));

        if (index === -1) {
            newAllocationChanges.push({
                sku,
                customer,
                allocation
            });
        } else {
            newAllocationChanges[index].allocation = allocation;
        }
        setAllocationChanges(newAllocationChanges);
    };

    const handleCellEditStop = (params, event) => {
        // console.log("onCellEditStop");
        // if (isNaN(params.formattedValue)) {
        //     if (!["TRUE", "FALSE"].includes(params.formattedValue.toUpperCase())) {
        //         props.toast.error("Allocation must be TRUE or FALSE or a number between 0 and 100");
        //         setErrorRows([...errorRows, selectedRowId]);
        //     }
        // } else {
        //     if (params.formattedValue < 0 || params.formattedValue > 100) {
        //         props.toast.error("Allocation must be TRUE or FALSE or a number between 0 and 100");
        //         setErrorRows([...errorRows, selectedRowId]);
        //     } else {
        //         const selectedRowAllocations = customers.map(customer => params.row[customer]);
        //         const numericAllocations = selectedRowAllocations.filter(allocation => !isNaN(allocation));
        //         const sumOfNumericAllocations = numericAllocations.reduce((x, y) => parseInt(x) + parseInt(y), 0);
        //         if (sumOfNumericAllocations > 100) {
        //             props.toast("Sum of allocation percentages for row must not exceed 100");
        //             setErrorRows([...errorRows, selectedRowId]);
        //         }
        //     }
        // }
    };

    const getRowClassName = params => {
        // if (errorRows.includes(params.id)) {
        //     return "red-table-row";
        // }

        if (params.id === selectedRowId) {
            return "blue-table-row";
        }

        return params.id === "displayed-allocation-row" ? "orange-table-row": "";
    };

    const search = () => {
        setSelectedRowId(-1);
        const searchResults = calculatedInventoryLevels.filter(skuInfo => {
            return skuInfo.sku.includes(searchValue) || skuInfo.upc.includes(searchValue);
        });
        setDisplayedRows(searchResults);
    };

    const reset = () => {
        setSelectedRowId(-1);
        setSearchValue("");
        setDisplayedRows(calculatedInventoryLevels);
    };

    const saveAllocationChanges = async () => {
        // if (errorRows.length) {
        //     props.toast.error("Please fix errors before saving");
        //     return;
        // }

        if (!allocationChanges.length) {
            props.toast.error("No changes to save");
            return;
        }

        props.setLoading(true);
        try {
            await inventoryHelpers.saveInventoryAllocations(allocationChanges, props.token);
            props.toast.success("Successfully saved inventory allocations");
            setAllocationChanges([]);
        } catch (error) {
            props.toast.error("Failed to save allocation changes, please contact a developer");
        }
        props.setLoading(false);
    };


    const inventoryAllocationTableColumns = [{
        ...defaultColumnAttributes,
        field: "sku",
        headerName: "SKU",
        minWidth: 140
    }, {
        ...defaultColumnAttributes,
        field: "upc",
        headerName: "UPC",
        minWidth: 140
    }, {
        ...defaultColumnAttributes,
        field: "itemStatus",
        headerName: "Item Status",
        minWidth: 110
    }, {
        ...defaultColumnAttributes,
        field: "inventoryLevel",
        headerName: "Inventory"
    }, {
        ...defaultColumnAttributes,
        field: "chewy",
        headerName: "Chewy",
        minWidth: 75,
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "amazonDirect",
        headerName: "Amazon Direct",
        minWidth: 120,
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "shopify",
        headerName: "Shopify",
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "target",
        headerName: "Target",
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "walmartMarketplace",
        headerName: "Walmart Marketplace",
        minWidth: 160,
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "wayfair",
        headerName: "Wayfair",
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "tractorSupply",
        headerName: "Tractor Supply",
        minWidth: 120,
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "academy",
        headerName: "Academy",
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "facebook",
        headerName: "Facebook",
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "kohls",
        headerName: "Kohl's",
        minWidth: 70,
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "lowes",
        headerName: "Lowes",
        minWidth: 70,
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "petco",
        headerName: "Petco",
        minWidth: 70,
        editable: true,
        valueParser: updateCustomerAllocation
    }, {
        ...defaultColumnAttributes,
        field: "petsmart",
        headerName: "PetSmart",
        editable: true,
        valueParser: updateCustomerAllocation
    }];

    const textFieldStyle = {
        display: "block",
        marginBottom: 1,
        marginLeft: 1
    };

    return (
        <Box className={props.isMobile ? "mobile-top" : "desktop-top"} sx={{ display: 'flex', flexDirection: 'column' }}>
            <InventoryAllocatorHelpPopup displayHelpModal={displayHelpModal} setDisplayHelpModal={setDisplayHelpModal} />

            <Box sx={{ marginLeft: "10px", width: "90%" }} className={props.isMobile ? "mobile-table-box" : "desktop-table-box"}>
                <h2>Inventory allocations</h2>
                <TextField sx={textFieldStyle} label="SKU/UPC search" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
                <div style={{marginBottom: 15, marginLeft: 5}}>
                    <Button sx={{ maxWidth: 120, margin: 0.5 }} onClick={search} variant="contained">Search</Button>
                    <Button sx={{ maxWidth: 120, margin: 0.5 }} onClick={reset} variant="contained">Reset</Button>
                    <Button sx={{ maxWidth: 120, margin: 0.5 }} onClick={() => setDisplayHelpModal(true)} variant="contained">Help</Button>
                    <span style={{marginLeft: "4%", fontSize: 12}}>*Inventory counts are updated daily at 2pm PST</span>
                    <Button sx={{ maxWidth: 120, margin: 0.5, float: "right", marginRight: "5%" }} onClick={saveAllocationChanges} variant="contained" color="success">Save</Button>
                </div>
                <DataGrid
                    style={{ width: "95%", marginLeft: 5 }}
                    rows={displayedRows}
                    className={props.isMobile ? "mobile-table" : "desktop-table"}
                    columns={inventoryAllocationTableColumns}
                    rowsPerPageOption={[25, 50, 100]}
                    checkboxSelection={false}
                    onRowClick={selectRow}
                    onCellEditStop={handleCellEditStop}
                    getRowClassName={getRowClassName}
                    isCellEditable={params => params.row.id === "displayed-allocation-row"}
                />
            </Box>
        </Box>
    )
};

export default InventoryAllocator;